import { useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";
import { requestAutomationSurveyPromptCheck } from "../utils/automationSurveyPromptBus";
import { workspaceScopedQueryKey } from "../utils/workspaceQueryScope";

function isPinOn(value) {
  return value === true || value === 1 || String(value) === "1";
}

function touchesPinFields(variables) {
  if (!variables || typeof variables !== "object") return false;
  return (
    Object.prototype.hasOwnProperty.call(variables, "fixada") ||
    Object.prototype.hasOwnProperty.call(variables, "fixada_usuario")
  );
}

function applyPinPatchToConversation(conv, variables) {
  if (!conv || variables?.id == null) return conv;
  if (String(conv.id) !== String(variables.id)) return conv;

  let fixada = Boolean(conv.fixada);
  let fixadaUsuario = Boolean(conv.fixadaUsuario);

  if (Object.prototype.hasOwnProperty.call(variables, "fixada")) {
    fixada = isPinOn(variables.fixada);
  }
  if (Object.prototype.hasOwnProperty.call(variables, "fixada_usuario")) {
    fixadaUsuario = isPinOn(variables.fixada_usuario);
  }

  return {
    ...conv,
    fixada,
    fixadaUsuario,
    pinned: fixada || fixadaUsuario,
  };
}

/** Atualiza `pinned`/`fixada*` em todas as páginas do infinite query de conversas. */
function patchConversationsCache(old, variables) {
  if (!old?.pages) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      list: (page.list || []).map((conv) =>
        applyPinPatchToConversation(conv, variables)
      ),
    })),
  };
}

export const useRequests = () => {
  const queryClient = useQueryClient();

  const updateMutation = useMutation(
    (request) => {
      const method = request.id ? "PUT" : "POST";
      const endpoint = request.id ? `/requests/${request.id}` : `/requests`;
      return fetchData(endpoint, method, request);
    },
    {
      onMutate: async (variables) => {
        if (!touchesPinFields(variables) || variables?.id == null) return undefined;

        await queryClient.cancelQueries("conversations");

        const previous = queryClient.getQueriesData("conversations");
        queryClient.setQueriesData("conversations", (old) =>
          patchConversationsCache(old, variables)
        );

        return { previous };
      },
      onError: (_err, variables, context) => {
        if (!context?.previous) return;
        context.previous.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
        // Garante reconciliar com o servidor se a mutação de pin falhar.
        if (touchesPinFields(variables)) {
          queryClient.invalidateQueries("conversations");
        }
      },
      onSuccess: (_data, variables) => {
        const id = variables?.id;

        // Espelha pin no cache (caso onMutate não tenha rodado / dados parciais).
        if (touchesPinFields(variables) && id != null) {
          queryClient.setQueriesData("conversations", (old) =>
            patchConversationsCache(old, variables)
          );
        }

        // Atualiza os detalhes da conversa atual (usa o mesmo padrão de chave do hook useConversationDetails)
        if (id != null) {
          queryClient.invalidateQueries(workspaceScopedQueryKey("conversationDetails", id));
          // Automação pode abrir pesquisa na tela após alteração (ex.: status → Perdida).
          window.setTimeout(() => requestAutomationSurveyPromptCheck(id), 200);
        } else {
          queryClient.invalidateQueries("conversationDetails");
          window.setTimeout(() => requestAutomationSurveyPromptCheck(null), 200);
        }

        // Atualiza a lista de conversas (ex.: chip de usuário/agente / status)
        queryClient.invalidateQueries("conversations");
      },
    }
  );

  return { updateMutation };
};
